import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Dimensions,
  Platform,
  ScrollView,
  Text,
  View,
} from 'react-native';
import { CouponDetails } from '../components/CouponDetails';
import { ProductAtGlance } from '../components/ProductAtGlance';
import { ProductBarrierInfo } from '../components/ProductBarrierInfo';
import { ProductHeader } from '../components/ProductHeader';
import { ProductPerformanceChart } from '../components/ProductPerformanceChart';
import { ProductUnderlyingsInfo } from '../components/ProductUnderlyingsInfo';
import { useProductConfig } from '../config/productTypes/useProductConfig';
import { COLORS } from '../constants/theme';
import {
  getPublicShareErrorMessage,
  getPublicSharedPriceHistory,
  getPublicSharedProduct,
  getPublicSharedUnderlyingsPriceHistory,
} from '../services/publicShareService';
import { PublicSharedProduct, PublicSharedUnderlyingHistorySeries } from '../types/productShare';
import {
  formatSharedUnderlyingsText,
  getSharedBasketTypeUp,
  getSharedDownside,
  getSharedUpside,
  mapPublicSharedChartProduct,
  mapPublicSharedProductForConfig,
} from '../utils/mapPublicSharedProduct';
import { isXl } from '../utils/responsiveStyles';
import { styles } from './SharedProductScreen.styles';

interface SharedProductScreenProps {
  readonly token: string;
}

type PerformanceTab = 'Week' | 'Month' | 'Year' | 'YTD' | 'All';

const { width } = Dimensions.get('window');

export const SharedProductScreen: React.FC<SharedProductScreenProps> = ({ token }) => {
  const [product, setProduct] = useState<PublicSharedProduct | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<PerformanceTab>('Year');
  const [fullPriceHistory, setFullPriceHistory] = useState<Array<{ date: string; price: number }>>(
    []
  );
  const [priceHistory, setPriceHistory] = useState<Array<{ date: string; price: number }>>([]);
  const [fullUnderlyingsHistory, setFullUnderlyingsHistory] = useState<
    PublicSharedUnderlyingHistorySeries[]
  >([]);
  const [underlyingsHistory, setUnderlyingsHistory] = useState<PublicSharedUnderlyingHistorySeries[]>(
    []
  );
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [visibleUnderlyings, setVisibleUnderlyings] = useState<boolean[]>([]);

  const configProduct = useMemo(
    () => (product ? mapPublicSharedProductForConfig(product) : null),
    [product]
  );

  const { shouldShow, sections } = useProductConfig(configProduct);

  const chartProduct = useMemo(
    () => (product ? mapPublicSharedChartProduct(product) : null),
    [product]
  );

  const atGlanceUnderlyingsText = useMemo(
    () => (product ? formatSharedUnderlyingsText(product.underlyings) : '-'),
    [product]
  );

  const filterPriceHistoryByPeriod = useCallback(() => {
    if (fullPriceHistory.length === 0) {
      setPriceHistory([]);
      setUnderlyingsHistory([]);
      return;
    }

    if (activeTab === 'All') {
      setPriceHistory(fullPriceHistory);
      setUnderlyingsHistory(fullUnderlyingsHistory);
      return;
    }

    const sortedFullHistory = [...fullPriceHistory].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
    const endDate = new Date(sortedFullHistory[0]?.date ?? Date.now());
    endDate.setHours(23, 59, 59, 999);

    let startDate: Date;
    switch (activeTab) {
      case 'Week':
        startDate = new Date(endDate.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'Month':
        startDate = new Date(endDate.getTime() - 30 * 24 * 60 * 60 * 1000);
        break;
      case 'Year':
        startDate = new Date(endDate.getTime() - 365 * 24 * 60 * 60 * 1000);
        break;
      case 'YTD':
        startDate = new Date(new Date().getFullYear(), 0, 1);
        break;
      default:
        setPriceHistory(fullPriceHistory);
        setUnderlyingsHistory(fullUnderlyingsHistory);
        return;
    }

    const filteredHistory = fullPriceHistory
      .filter((item) => {
        const itemDate = new Date(item.date);
        return (
          !Number.isNaN(itemDate.getTime()) &&
          itemDate.getTime() >= startDate.getTime() &&
          itemDate.getTime() <= endDate.getTime()
        );
      })
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    setPriceHistory(filteredHistory);

    if (fullUnderlyingsHistory.length > 0) {
      const filteredUnderlyings = fullUnderlyingsHistory.map((underlying) => {
        if (!underlying.history.length) {
          return underlying;
        }

        const filteredUnderlyingHistory = underlying.history
          .filter((historyPoint) => {
            const itemDate = new Date(historyPoint.date);
            return (
              !Number.isNaN(itemDate.getTime()) &&
              itemDate.getTime() >= startDate.getTime() &&
              itemDate.getTime() <= endDate.getTime()
            );
          })
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        return { ...underlying, history: filteredUnderlyingHistory };
      });

      setUnderlyingsHistory(filteredUnderlyings);
    } else {
      setUnderlyingsHistory([]);
    }
  }, [activeTab, fullPriceHistory, fullUnderlyingsHistory]);

  useEffect(() => {
    filterPriceHistoryByPeriod();
  }, [filterPriceHistoryByPeriod]);

  useEffect(() => {
    const loadSharedProduct = async () => {
      try {
        setLoading(true);
        setError(null);
        setLoadingHistory(true);

        const [productData, historyData, underlyingsHistoryData] = await Promise.all([
          getPublicSharedProduct(token),
          getPublicSharedPriceHistory(token, 'all'),
          getPublicSharedUnderlyingsPriceHistory(token, 'all').catch(() => []),
        ]);

        setProduct(productData);
        setVisibleUnderlyings(new Array(productData.underlyings.length).fill(true));

        const sortedHistory = historyData
          .map((item) => ({ date: item.date, price: item.price }))
          .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        setFullPriceHistory(sortedHistory);
        setFullUnderlyingsHistory(underlyingsHistoryData);
        setActiveTab(productData.indicators.tenorMonths && productData.indicators.tenorMonths > 12 ? 'All' : 'Year');
      } catch (loadError: unknown) {
        setError(getPublicShareErrorMessage(loadError));
        setProduct(null);
        setFullPriceHistory([]);
        setFullUnderlyingsHistory([]);
        setUnderlyingsHistory([]);
        setVisibleUnderlyings([]);
      } finally {
        setLoading(false);
        setLoadingHistory(false);
      }
    };

    loadSharedProduct();
  }, [token]);

  const calculatePerformance = useCallback((): string => {
    if (!product || product.bid == null) {
      return '0.00';
    }

    const issuePrice = Number(product.indicators.issuePrice);

    if (activeTab === 'All') {
      if (!Number.isFinite(issuePrice) || issuePrice === 0) {
        return '0.00';
      }

      const perfSinceIssue = ((Number(product.bid) - issuePrice) / issuePrice) * 100;
      return Number.isFinite(perfSinceIssue) ? perfSinceIssue.toFixed(2) : '0.00';
    }

    if (activeTab === 'YTD' && product.indicators.issueDate) {
      const issueDate = new Date(product.indicators.issueDate);
      const currentYear = new Date().getFullYear();

      if (
        !Number.isNaN(issueDate.getTime()) &&
        issueDate.getFullYear() === currentYear &&
        Number.isFinite(issuePrice) &&
        issuePrice !== 0
      ) {
        const perfSinceIssue = ((Number(product.bid) - issuePrice) / issuePrice) * 100;
        return Number.isFinite(perfSinceIssue) ? perfSinceIssue.toFixed(2) : '0.00';
      }
    }

    if (priceHistory.length >= 2) {
      const first = Number(priceHistory[0].price);
      const last = Number(priceHistory[priceHistory.length - 1].price);

      if (Number.isFinite(first) && Number.isFinite(last) && first !== 0) {
        const perfRange = ((last / first) - 1) * 100;
        return Number.isFinite(perfRange) ? perfRange.toFixed(2) : '0.00';
      }
    }

    if (!Number.isFinite(issuePrice) || issuePrice === 0) {
      return '0.00';
    }

    const performance = Number(product.bid) - issuePrice;
    return Number.isFinite(performance) ? performance.toFixed(2) : '0.00';
  }, [activeTab, priceHistory, product]);

  const toggleUnderlyingVisibility = (index: number) => {
    setVisibleUnderlyings((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const renderPerformanceChart = (isDesktopCombined: boolean) => (
    <View style={styles.section}>
      <ProductPerformanceChart
        activeTab={activeTab}
        onTabChange={setActiveTab}
        priceHistory={priceHistory}
        fullPriceHistory={fullPriceHistory}
        underlyingsHistory={underlyingsHistory}
        fullUnderlyingsHistory={fullUnderlyingsHistory}
        visibleUnderlyings={visibleUnderlyings}
        loadingHistory={loadingHistory}
        product={chartProduct}
        calculatePerformance={calculatePerformance}
        isDesktopCombined={isDesktopCombined}
        showUnderlyingsInChart={
          sections.performance.props.showUnderlyingsInChart !== false &&
          fullUnderlyingsHistory.length > 0
        }
        showTooltipPrice={sections.performance.props.showTooltipPrice !== false}
      />
    </View>
  );

  const renderAtGlance = () => {
    if (!product || !shouldShow.atGlance) {
      return null;
    }

    const { indicators } = product;

    return (
      <ProductAtGlance
        productCode={product.productCode ?? undefined}
        productType={indicators.productTypeName || indicators.productType || '-'}
        issuer={indicators.issuer || '-'}
        showIssuer={sections.atGlance.props.Issuer !== false}
        underlyings={atGlanceUnderlyingsText}
        issuePrice={indicators.issuePrice}
        showIssuePrice={sections.atGlance.props.issuePrice !== false}
        tenorMonths={indicators.tenorMonths}
        coupon={indicators.coupon}
        denomination={indicators.denomination}
        currency={product.currency}
        initialFixing={indicators.initialFixing}
        issueDate={indicators.issueDate}
        finalFixing={indicators.finalFixing}
        redemptionDate={indicators.redemptionDate}
        couponBarrier={indicators.couponBarrier}
        showCouponBarrier={sections.atGlance.props.couponBarrier}
        couponMemory={indicators.couponMemory}
        showCouponMemory={sections.atGlance.props.couponMemory}
        showCoupon={sections.atGlance.props.coupon}
        upside={getSharedUpside(indicators)}
        showUpside={sections.atGlance.props.upside}
        upsideParticipation={indicators.upsideParticipation}
        showUpsideParticipation={sections.atGlance.props.upsideParticipation}
        downside={getSharedDownside(indicators)}
        showDownside={sections.atGlance.props.downside}
        downsideParticipation={indicators.downsideParticipation}
        showDownsideParticipation={sections.atGlance.props.downsideParticipation}
        showSettlementType={sections.atGlance.props.settlementType}
        settlementType={indicators.settlementTypeName}
        showGrade={sections.atGlance.props.grade}
        grade={indicators.grade}
        showRecovery={sections.atGlance.props.recovery}
        recovery={indicators.recovery}
        basketTypeUp={getSharedBasketTypeUp(indicators)}
        strikeLevel={indicators.strikeLevel ?? 0}
        bonusLevel={indicators.bonusLevel ?? 0}
        capLevel={indicators.capLevel ?? 0}
        maxReturn={indicators.maxReturn ?? 0}
        tradedNotional={0}
        showTradedNotional={false}
        currentNotional={null}
        showCurrentNotional={false}
        atGlanceProps={sections.atGlance.props}
        showPremium={sections.atGlance.props.premium}
        premium={indicators.premiumPct}
        showQuotationType={sections.atGlance.props.quotationType}
        quotationType={indicators.quotationType}
        showStrike={sections.atGlance.props.strike}
        strike={indicators.strikeLevel}
        showBasketOptionType={sections.atGlance.props.basketOptionType}
        basketOptionType={null}
        showUpperStrike={sections.atGlance.props.upperStrike}
        upperStrike={indicators.upperStrikePct}
        showLowerStrike={sections.atGlance.props.lowerStrike}
        showBarrierType={sections.atGlance.props.barrierType}
        barrierType={indicators.barrierStyleName}
        showLowerBarrier={sections.atGlance.props.lowerBarrier}
        lowerBarrier={indicators.barrierLevel}
        showUpperBarrier={sections.atGlance.props.upperBarrier}
        upperBarrier={indicators.barrierUpper}
        showDenomination={sections.atGlance.props.denomination}
        showLeverage={sections.atGlance.props.leverage}
        leverage={indicators.leverage}
        showRatio={sections.atGlance.props.ratio}
        ratio={indicators.ratio}
        showStopLoss={sections.atGlance.props.stopLoss}
        stopLoss={indicators.stopLoss}
        showCouponTrigger={sections.atGlance.props.couponTrigger}
        couponTrigger={indicators.couponTrigger}
        showCouponFrequency={sections.atGlance.props.couponFrequency}
        couponFrequency={indicators.couponFrequencyName}
        showCouponType={sections.atGlance.props.couponType}
        couponType={indicators.couponTypeName}
        showCapitalProtection={sections.atGlance.props.capitalProtectionPct}
        capitalProtection={indicators.capitalProtectionPct}
        showCouponStepUp={sections.atGlance.props.stepUp}
        couponStepUp={indicators.couponStepUp}
        showCouponMinPct={sections.atGlance.props.couponMinimum}
        couponMinPct={indicators.couponMinPct}
        showCouponMaxPct={sections.atGlance.props.couponMaximum}
        couponMaxPct={indicators.couponMaxPct}
      />
    );
  };

  const renderBarrierInfo = () => {
    if (!product || !shouldShow.barrier) {
      return null;
    }

    const { indicators } = product;

    return (
      <ProductBarrierInfo
        barrierStyleName={indicators.barrierStyleName || 'No barrier'}
        barrierHitRecorded={indicators.barrierHitRecorded}
        strikeLevel={indicators.strikeLevel}
        barrierLevel={indicators.barrierLevel}
        underlyings={product.underlyings}
        couponMemory={indicators.couponMemory}
        showCouponMemory={sections.barrier.props.couponMemory}
        showDistanceCouponBarrier={sections.barrier.props.distanceCouponBarrier}
        couponBarrier={indicators.couponBarrier}
        barrierProps={sections.barrier.props}
        sectionTitle={sections.barrier.title}
        showDistanceStopLoss={sections.barrier.props.distanceStopLoss}
        distanceStopLoss={indicators.stopLossDistance}
        showStoppLoss={sections.barrier.props.stopLoss}
        stopLoss={indicators.stopLoss}
      />
    );
  };

  const renderCouponDetails = () => {
    if (!product || !shouldShow.couponDetails) {
      return null;
    }

    const { indicators } = product;

    return (
      <CouponDetails
        coupon={indicators.coupon}
        couponFrequency={indicators.couponFrequencyName}
        couponMinPct={indicators.couponMinPct}
        couponMaxPct={indicators.couponMaxPct}
        couponType={indicators.couponTypeName}
        referenceRate={null}
        couponDetailsProps={sections.couponDetails.props}
      />
    );
  };

  const renderUnderlyingsInfo = (isDesktopCombined: boolean) => {
    if (!product || !shouldShow.underlyings) {
      return null;
    }

    return (
      <ProductUnderlyingsInfo
        underlyings={product.underlyings}
        visibleUnderlyings={visibleUnderlyings}
        currency={product.currency}
        onToggleVisibility={toggleUnderlyingVisibility}
        isDesktopCombined={isDesktopCombined}
        showDistanceCouponBarrier={sections.underlyings.props.distanceCouponBarrier}
        showBarrierLevel={sections.underlyings.props.barrierLevel !== false}
        showBarrierDistance={sections.underlyings.props.barrierDistance !== false}
        showAutocallDistance={sections.underlyings.props.autocallDistance === true}
        underlyingsProps={sections.underlyings.props}
        showUpperStrike={sections.underlyings.props.upperStrike}
        showStrikeLevel={sections.underlyings.props.strikeLevel}
        showStrikeDistance={sections.underlyings.props.strikeDistance}
      />
    );
  };

  if (loading) {
    return (
      <View style={styles.centerContainer}>
        <ActivityIndicator size="large" color={COLORS.primary} />
        <Text style={styles.loadingText}>Loading shared product...</Text>
      </View>
    );
  }

  if (error || !product) {
    return (
      <View style={styles.centerContainer}>
        <Text style={styles.errorText}>{error || 'Shared product unavailable'}</Text>
        <Text style={styles.errorHint}>This link may be invalid, expired, or revoked.</Text>
      </View>
    );
  }

  return (
    <View style={[styles.container, Platform.OS === 'web' && { padding: 0, margin: 0 }]}>
      <View style={[styles.header, width >= 1024 && styles.headerDesktop]}>
        <View style={styles.badge}>
          <Text style={styles.badgeText}>Shared view</Text>
        </View>
        <Text style={styles.headerTitle}>{product.name}</Text>
        <Text style={styles.headerSubtitle}>Read-only access via secure link</Text>
      </View>

      <ScrollView
        style={[styles.scrollView, Platform.OS === 'web' && { padding: 0, margin: 0 }]}
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
      >
        <ProductHeader
          name={product.name}
          currency={product.currency}
          bid={product.bid}
          ask={product.ask}
          valor={product.valor ?? '-'}
          isin={product.isin}
          showDocumentActions={false}
        />

        {width >= 1024 ? (
          <View style={styles.desktopContainer}>
            {renderAtGlance()}
            {renderBarrierInfo()}
            {renderCouponDetails()}
            {shouldShow.performance && renderPerformanceChart(true)}
            {renderUnderlyingsInfo(true)}
          </View>
        ) : isXl(width) ? (
          <>
            {renderAtGlance()}
            <View style={styles.tabletRow}>
              <View style={styles.tabletColumn}>
                {renderBarrierInfo()}
                {renderCouponDetails()}
              </View>
              <View style={styles.tabletColumn}>
                {shouldShow.performance && renderPerformanceChart(false)}
              </View>
            </View>
            {renderUnderlyingsInfo(false)}
          </>
        ) : (
          <>
            {renderAtGlance()}
            {renderBarrierInfo()}
            {renderCouponDetails()}
            {shouldShow.performance && renderPerformanceChart(false)}
            {renderUnderlyingsInfo(false)}
          </>
        )}
      </ScrollView>
    </View>
  );
};
